<template>
  <div v-if="visible" class="voucher-modal-overlay" @click.self="$emit('close')">
    <div class="voucher-modal" :dir="isHebrew ? 'rtl' : 'ltr'">

      <!-- Header -->
      <div class="voucher-modal-header">
        <span class="voucher-modal-counter">
          {{ vouchers.length > 1 ? (t('voucherPlural') + ' ' + (currentIndex + 1) + ' / ' + vouchers.length) : t('voucher') }}
        </span>
        <div class="voucher-modal-header-actions">
          <button class="voucher-modal-download" :disabled="downloading" @click="download" :title="t('download') || 'Download'">
            <span class="material-icons" style="font-size:20px;">{{ downloading ? 'hourglass_empty' : 'download' }}</span>
          </button>
          <button class="voucher-modal-close" @click="$emit('close')">✕</button>
        </div>
      </div>

      <!-- Content -->
      <div
        class="voucher-modal-body"
        @touchstart="onTouchStart"
        @touchend="onTouchEnd"
      >
        <template v-if="isImage(currentUrl)">
          <img :src="currentUrl" class="voucher-modal-img" alt="voucher" />
        </template>
        <template v-else>
          <div class="voucher-modal-pdf">
            <span class="material-icons voucher-modal-pdf-icon">picture_as_pdf</span>
            <a :href="currentUrl" target="_blank" rel="noopener noreferrer" class="voucher-modal-pdf-link">
              {{ t('viewPdf') || 'Open PDF' }}
            </a>
          </div>
        </template>
      </div>

      <!-- Navigation — always ltr so left=prev, right=next in all languages -->
      <div v-if="vouchers.length > 1" class="voucher-modal-nav" dir="ltr">
        <button class="voucher-modal-arrow" @click="prev">&#8249;</button>
        <button class="voucher-modal-arrow" @click="next">&#8250;</button>
      </div>

    </div>
  </div>
</template>

<script>
import { translationMixin } from '@/mixins/translationMixin';
import { voucherFilename } from '@/utils/voucherFileName';

export default {
  name: 'VoucherViewerModal',
  mixins: [translationMixin],
  props: {
    vouchers: {
      type: Array,
      default: () => []
    },
    visible: {
      type: Boolean,
      default: false
    },
    startIndex: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      currentIndex: 0,
      touchStartX: 0,
      downloading: false,
    };
  },
  computed: {
    currentUrl() {
      return this.vouchers[this.currentIndex] || '';
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.currentIndex = this.startIndex || 0;
        document.addEventListener('keydown', this.onKeyDown);
      } else {
        document.removeEventListener('keydown', this.onKeyDown);
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeyDown);
  },
  methods: {
    isImage(url) {
      if (!url) return false;
      const lower = url.toLowerCase().split('?')[0];
      return ['.jpg', '.jpeg', '.png', '.gif', '.webp'].some(ext => lower.endsWith(ext));
    },
    next() {
      this.currentIndex = (this.currentIndex + 1) % this.vouchers.length;
    },
    prev() {
      this.currentIndex = (this.currentIndex - 1 + this.vouchers.length) % this.vouchers.length;
    },
    onKeyDown(e) {
      if (e.key === 'ArrowRight') this.next();
      if (e.key === 'ArrowLeft')  this.prev();
      if (e.key === 'Escape') this.$emit('close');
    },
    async download() {
      if (!this.currentUrl || this.downloading) return;
      this.downloading = true;
      try {
        const response = await fetch(this.currentUrl);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = this.getFilename(this.currentUrl);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      } catch {
        window.open(this.currentUrl, '_blank');
      } finally {
        this.downloading = false;
      }
    },
    getFilename(url) {
      return voucherFilename(url, 'voucher');
    },
    onTouchStart(e) {
      this.touchStartX = e.changedTouches[0].clientX;
    },
    onTouchEnd(e) {
      const diff = this.touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 50) return;
      if (diff > 0) this.next();
      else          this.prev();
    },
  }
};
</script>

<style scoped>
.voucher-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.voucher-modal {
  background: #fff;
  border-radius: 16px;
  width: 90vw;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.4);
}

@media (min-width: 768px) {
  .voucher-modal {
    width: 80vw;
    max-width: 1000px;
    max-height: 85vh;
  }
}

.voucher-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.voucher-modal-counter {
  font-size: 1.4rem;
  color: #666;
  font-weight: 600;
}

.voucher-modal-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.voucher-modal-download,
.voucher-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 6px 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.voucher-modal-close {
  font-size: 20px;
}

.voucher-modal-download:hover,
.voucher-modal-close:hover {
  background: #f0f0f0;
  color: #333;
}

.voucher-modal-download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.voucher-modal-body {
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  flex: 1 1 60vh;
  min-height: 0;
  user-select: none;
}

.voucher-modal-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.voucher-modal-pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.voucher-modal-pdf-icon {
  font-size: 64px;
  color: #07A0C3;
}

.voucher-modal-pdf-link {
  font-size: 1.6rem;
  color: #07A0C3;
  text-decoration: none;
  font-weight: 600;
  padding: 10px 24px;
  border: 2px solid #07A0C3;
  border-radius: 10px;
  transition: all 0.2s;
}

.voucher-modal-pdf-link:hover {
  background: #07A0C3;
  color: #fff;
}

.voucher-modal-nav {
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid #eee;
  flex-shrink: 0;
}

.voucher-modal-arrow {
  background: #f5f5f5;
  border: none;
  font-size: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  line-height: 1;
}

.voucher-modal-arrow:hover {
  background: #e0e0e0;
}
</style>
